"use client";

import { CSSProperties, useRef, useState } from "react";
import { storyblokEditable } from "@storyblok/react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import clsx from "clsx";
import Image from "next/image";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import LiveDot from "@/components/LiveDot";
import { sbBlok } from "@/types/sb-types";
import type { SeasonSummaryData } from "@/lib/seasonSummary";
import { PAST_SEASON_SUMMARIES } from "@/utils/constants";
import { getCurrentSeason, SEASON_IDS } from "@/utils/tournaments";

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

// RGB triplets of tm-c-green (current season) and tm-c-brand (past seasons).
const ACCENT = { current: "34 197 94", past: "236 105 26" };

const formatPurse = (purse: number) => `$${purse.toLocaleString("en-US")}`;

// Oversized season number: solid digits, echoes behind them, faded out to the left.
const NUMBER_FADE =
  "[mask-image:linear-gradient(to_right,transparent_12%,rgb(0_0_0/0.12)_28%,rgb(0_0_0/0.4)_42%,rgb(0_0_0/0.75)_54%,#000_66%)]";
const NUMBER_ECHOES =
  "[text-shadow:-0.12em_0_0_#282828,-0.24em_0_0_#202020,-0.36em_0_0_#1a1a1a]";

const Stat = ({
  label,
  value,
  isLoading,
}: {
  label: string;
  value: string;
  isLoading: boolean;
}) => (
  <div className="flex flex-col items-center gap-1 whitespace-nowrap 2xl:w-32.5 2xl:shrink-0">
    <p className="m-0 text-[10px] text-center text-tm-c-gray-500 md:text-xs md:font-medium">
      {label}
    </p>
    {isLoading ? (
      // Same height as the value line, so nothing shifts when it loads.
      <div
        aria-hidden
        className="h-6 w-20 animate-pulse rounded bg-tm-c-gray-900 md:h-7 2xl:h-8"
      />
    ) : (
      <p className="m-0 text-base font-bold text-white md:text-xl 3xl:text-2xl">
        {value}
      </p>
    )}
  </div>
);

const WinnerName = ({ name }: { name: string }) => {
  const ref = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = () => {
    const el = ref.current;
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
  };

  return (
    <div className="group relative min-w-0" onMouseEnter={checkTruncation}>
      <p
        ref={ref}
        className="m-0 truncate text-base font-bold tracking-[0.5px] text-white md:text-xl 3xl:text-2xl"
      >
        {name}
      </p>
      {isTruncated && (
        <span
          role="tooltip"
          className="pointer-events-none absolute bottom-full left-0 z-10 mb-2 hidden w-max max-w-80 rounded-sm border border-solid border-white/40 bg-[var(--background,#090909)] px-2 py-1 text-xs font-medium text-white opacity-0 shadow-[4px_4px_4px_0_rgba(9,9,9,0.25)] transition-opacity group-hover:opacity-100 2xl:block"
        >
          {name}
        </span>
      )}
    </div>
  );
};

const SeasonSummaryCard = ({
  blok,
  currentSeasonData,
}: sbBlok & { currentSeasonData?: SeasonSummaryData }) => {
  const requested = Number(blok?.season_number);
  const currentSeason = getCurrentSeason();
  const season = SEASON_IDS.includes(requested) ? requested : currentSeason;
  const isCurrent = season === currentSeason;

  // Finished seasons come from constants (no request); the current season is
  // prefetched on the server and then refreshed on the client.
  const initialData = isCurrent
    ? currentSeasonData
    : PAST_SEASON_SUMMARIES[season];

  const { data, isLoading } = useQuery({
    initialData,
    queryKey: ["season-summary", season],
    queryFn: async () =>
      (
        await axios.get<SeasonSummaryData>("/api/season-summary", {
          params: { season },
        })
      ).data,
    // Only the running season changes; refresh it every 5 minutes.
    refetchInterval: isCurrent ? REFRESH_INTERVAL_MS : false,
    staleTime: isCurrent ? REFRESH_INTERVAL_MS : Infinity,
  });

  // Winner is entered on the card in Storyblok and only shown for past seasons.
  const winnerName = !isCurrent ? (blok?.winner_name as string) : "";
  const winnerImage = blok?.winner_image;

  const stats = [
    { label: "Season purse", value: data && formatPurse(data.purse) },
    { label: "Players", value: data?.players.toLocaleString("en-US") },
    { label: "Tournaments", value: data?.tournaments.toString() },
  ];

  return (
    <div {...storyblokEditable(blok)}>
      <section
        aria-label={`Season ${season} summary`}
        style={
          {
            "--accent": isCurrent ? ACCENT.current : ACCENT.past,
          } as CSSProperties
        }
        className={clsx(
          "relative flex flex-col gap-5 overflow-hidden rounded-[10px] border border-solid border-[rgb(var(--accent)/0.2)] bg-tm-c-gray-950 p-5 md:p-6.25 2xl:flex-row 2xl:items-center 2xl:p-10",
          // No winner yet: header on the left, stats on the right, one row from md.
          !winnerName && "md:flex-row md:items-center md:justify-between",
        )}
      >
        {/* Background: season number with the accent glow above it, so the glow tints the digits */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 [container-type:size]"
        >
          <span
            className={clsx(
              "absolute -right-[2%] top-1/2 -translate-y-1/2 select-none pl-[0.45em] text-[170cqh] leading-none font-bold italic text-[#323232]",
              NUMBER_FADE,
              NUMBER_ECHOES,
            )}
          >
            {season}
          </span>
          <div className="absolute -left-18.5 -top-28.75 size-96.5 rounded-[110px] bg-[radial-gradient(closest-side,rgb(var(--accent)/0.2),rgb(var(--accent)/0))] md:-left-32 md:-top-61.75 md:h-118.75 md:w-108 2xl:-left-37.5 2xl:-top-115.25 2xl:h-180.25 2xl:w-168.5" />
        </div>

        <header
          className={clsx(
            "relative flex flex-col gap-1 2xl:shrink-0",
            winnerName ? "2xl:w-55" : "2xl:w-85",
          )}
        >
          <div
            className={clsx(
              "flex items-center gap-1 text-[10px] leading-3 font-medium uppercase md:text-xs md:leading-4 md:tracking-[0.36px]",
              isCurrent ? "text-tm-c-green" : "text-tm-c-brand",
            )}
          >
            {isCurrent && <LiveDot />}
            {isCurrent ? "Current season" : "Season history"}
          </div>
          <h2 className="m-0 text-2xl font-normal text-white 2xl:text-3xl 3xl:text-4xl">
            Season {season}
          </h2>
        </header>

        <div
          className={clsx(
            "relative flex flex-col gap-5 md:flex-row md:items-center md:justify-between",
            "2xl:min-w-0 2xl:flex-1 2xl:justify-end 3xl:gap-6",
            !winnerName && "md:justify-end md:gap-10",
          )}
        >
          {!!winnerName && (
            <div className="order-first flex min-w-0 items-center gap-2 md:order-last">
              <div className="relative h-11 w-16 shrink-0 overflow-hidden rounded-sm bg-tm-c-gray-800 2xl:h-16 2xl:w-23.75">
                <Image
                  src={winnerImage?.filename || "/icons/avatar_m.svg"}
                  alt={winnerImage?.alt || winnerName}
                  fill
                  sizes="93px"
                  className="object-cover"
                />
              </div>
              <div className="flex min-w-0 flex-col gap-1">
                <div className="flex items-center gap-1 text-tm-c-gray-500">
                  <EmojiEventsOutlinedIcon
                    aria-hidden
                    sx={{ fontSize: 14, color: "var(--color-tm-c-brand)" }}
                  />
                  <span className="whitespace-nowrap text-[10px] font-normal md:text-xs md:font-medium">
                    Order of Merit winner
                  </span>
                </div>
                <WinnerName name={winnerName} />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between md:contents">
            {stats.map(({ label, value }) => (
              <Stat
                key={label}
                label={label}
                value={value ?? "–"}
                isLoading={isLoading}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default SeasonSummaryCard;
